/**
 * Utility functions for mapping calendar months to ISO week ranges
 * Handles academic years that cross calendar years
 */

import { getWeekNumber } from './helpers.ts';

/**
 * Maps calendar months to approximate ISO week ranges
 * Note: These are approximate ranges as months don't align perfectly with ISO weeks
 */
export const MONTH_TO_WEEK_MAPPING = {
    January: { start: 1, end: 5 },
    February: { start: 6, end: 9 },
    March: { start: 10, end: 13 },
    April: { start: 14, end: 17 },
    May: { start: 18, end: 22 },
    June: { start: 23, end: 26 },
    July: { start: 27, end: 30 },
    August: { start: 31, end: 35 },
    September: { start: 36, end: 39 },
    October: { start: 40, end: 43 },
    November: { start: 44, end: 47 },
    December: { start: 48, end: 53 }
};

/**
 * Gets more precise week ranges for a month in a specific year
 * @param {string} month - Month name (e.g., "January")
 * @param {number} year - Calendar year
 * @returns {Object} - {start: number, end: number} week range
 */
export const getPreciseWeekRangeForMonth = (month, year) => {
    const monthIndex = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ].indexOf(month);

    if (monthIndex === -1) {
        console.warn(`Invalid month name: ${month}`);
        return MONTH_TO_WEEK_MAPPING[month] || { start: 1, end: 4 };
    }

    // Get first and last day of the month
    const firstDay = new Date(year, monthIndex, 1);
    const lastDay = new Date(year, monthIndex + 1, 0); // Last day of month

    // Get ISO week numbers
    const startWeek = getWeekNumber(firstDay);
    const endWeek = getWeekNumber(lastDay);

    return { start: startWeek, end: endWeek };
};

/**
 * Converts a month selection to a week series string
 * @param {string} month - Month name
 * @param {number} calendarYear - Calendar year for the month
 * @returns {string} - Week series string (e.g., "1-5")
 */
export const monthToWeekSeries = (month, calendarYear) => {
    const range = getPreciseWeekRangeForMonth(month, calendarYear);
    
    if (range.start === range.end) {
        return range.start.toString();
    }
    
    return `${range.start}-${range.end}`;
};

/**
 * Gets all months with their week ranges for an academic year, ordered by academic year sequence
 * @param {Object} academicYearStructure - Academic year structure from API
 * @returns {Array} - Array of month objects with week ranges, ordered by academic year
 */
export const getMonthsForAcademicYear = (academicYearStructure) => {
    if (!academicYearStructure) {
        // Fallback to standard calendar year order
        return Object.entries(MONTH_TO_WEEK_MAPPING).map(([month, range]) => ({
            name: month,
            weekRange: range,
            weekSeries: range.start === range.end ? range.start.toString() : `${range.start}-${range.end}`,
            calendarYear: new Date().getFullYear(),
            type: 'month'
        }));
    }

    const { academicYear, startWeek, endWeek } = academicYearStructure;
    const ayStr = academicYear && String(academicYear).includes('-')
        ? academicYear
        : `${new Date().getFullYear()}-${new Date().getFullYear() + 1}`;
    const yearParts = ayStr.split('-');
    const startCalendarYear = parseInt(yearParts[0], 10);
    const endCalendarYear = parseInt(yearParts[1], 10);

    const months = [];
    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    // Determine academic year order based on start week
    let academicMonthOrder;
    if (startWeek >= 36) {
        // Academic year starts around September (week 36)
        academicMonthOrder = [
            'September', 'October', 'November', 'December',
            'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August'
        ];
    } else if (startWeek >= 1 && startWeek <= 8) {
        // Academic year starts around January
        academicMonthOrder = monthNames;
    } else {
        // Default fallback
        academicMonthOrder = monthNames;
    }

    // Process months in academic year order
    academicMonthOrder.forEach(month => {
        const range = MONTH_TO_WEEK_MAPPING[month];
        if (!range) return;

        let calendarYear;

        // Determine which calendar year this month belongs to based on academic year structure
        if (startWeek <= endWeek) {
            // Normal academic year (doesn't cross calendar years)
            calendarYear = startCalendarYear;
        } else {
            // Cross-year academic year
            if (range.start >= startWeek || range.end >= startWeek) {
                // Month is in the first part of academic year (current calendar year)
                calendarYear = startCalendarYear;
            } else if (range.start <= endWeek || range.end <= endWeek) {
                // Month is in the second part of academic year (next calendar year)
                calendarYear = endCalendarYear;
            } else {
                // Default to start year
                calendarYear = startCalendarYear;
            }
        }

        // Get more precise week range for this specific calendar year
        const preciseRange = getPreciseWeekRangeForMonth(month, calendarYear);
        
        months.push({
            name: month,
            weekRange: preciseRange,
            weekSeries: preciseRange.start === preciseRange.end 
                ? preciseRange.start.toString() 
                : `${preciseRange.start}-${preciseRange.end}`,
            calendarYear,
            type: 'month'
        });
    });

    return months;
};

/**
 * Formats academic year for display
 * @param {number} startYear - Start calendar year
 * @returns {string} - Formatted academic year (e.g., "2025-2026")
 */
export const formatAcademicYear = (startYear) => {
    return `${startYear}-${startYear + 1}`;
};

/**
 * Parses academic year string to get start year
 * @param {string} academicYear - Academic year string (e.g., "2025-2026")
 * @returns {number} - Start calendar year
 */
export const parseAcademicYear = (academicYear) => {
    const parts = academicYear.split('-');
    return parseInt(parts[0], 10);
}; 